import React, {useEffect, useState} from 'react';
import {Button, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';

import {StatisticsMode, statisticsQuery, statisticsService} from '@store/statistics';

import {Download} from '@icons/index';

import {DashboardRoutes, useRouteMatch} from '@utils/routes.utils';
import dayjs from '@utils/dayjs.utils';

import StatisticsCard from '@components/statistics/cards/Statistics.card';
import SelectComponent from '@components/inputs/select/Select.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {useObservable} from '@ngneat/react-rxjs';
import {Sizes} from '@constants/sizes.constants';
import {forkJoin} from 'rxjs';
import {NumberExt} from '@utils/extensions/number.extension';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';

const StatsTab = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const currentTab = useRouteMatch(DashboardRoutes.statsRoutes());

  const [filters] = useObservable(statisticsQuery.filters$);

  const [{totalTurnoverNew, loading: turnoverLoading}] = useObservable(statisticsQuery.totalTurnoverNew$);
  const [{evolutionOfOperations, loading: operationLoading}] = useObservable(statisticsQuery.evolutionOfOperations$);

  useEffect(() => {
    if (!filters.date || dayjs(filters.date).isValid()) {
      forkJoin([
        statisticsService.getEvolutionOfOperations(filters),
        statisticsService.getTurnoverAndPurchase(filters),
        statisticsService.getTotalTurnoverNew(filters),
        statisticsService.getInvoicingProgression(filters),
        statisticsService.getMarginByLine(filters),
        statisticsService.getTurnoverChargesByServiceKind(filters),
        statisticsService.getValoWeigtByWasteFamily(filters),
        statisticsService.getExecutionCountByServiceKind(filters),
        statisticsService.getMostProfitableClients(filters),
      ]).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, filters]);

  return (
    <Stack flex={1} width="100%" spacing={1} mt="-35px" overflow="hidden">
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} width="500px">
            <SelectComponent
              size="small"
              inputWidth={100}
              value={filters.mode}
              handleChange={(mode) => statisticsService.setFilters({mode})}
              items={StatisticsMode.selectItems()}
            />
            <DatePickerComponent
              size="small"
              inputWidth={Sizes.datePickerWidth}
              handleChange={(date) => statisticsService.setFilters({date})}
              value={filters.date}
            />
          </Stack>
          <TableFiltersComponent
            filters={filters}
            handleSave={(tableFilters) => statisticsService.setFilters({tableFilters})}
            handleReset={() => {
              statisticsService.resetFilters();
            }}
          />
        </Stack>
      </Stack>
      <Stack spacing={3} overflow="auto" className="scrollable" pt="15px" pb="25px" pr="10px">
        <Stack direction="row" spacing={3}>
          <StatisticsCard
            loading={turnoverLoading}
            selected={currentTab === DashboardRoutes.FINANCE}
            handleClick={() => navigate(DashboardRoutes.FINANCE)}
            title={t('dashboard.statistics.finance.title')}
            description={t('dashboard.statistics.finance.description')}
            unit=""
            value={
              NumberExt.toCurrency(Math.round((totalTurnoverNew?.turnover || 0)) /
                100)
            }
          />
          <StatisticsCard
            loading={operationLoading}
            selected={currentTab === DashboardRoutes.OPERATIONS}
            handleClick={() => navigate(DashboardRoutes.OPERATIONS)}
            title={t('dashboard.statistics.operations.title')}
            description={t('dashboard.statistics.operations.description')}
            unit={t('dashboard.statistics.operations.unit')}
            value={evolutionOfOperations?.execExecutedPickupCount.current || 0}
          />
        </Stack>
        <Outlet/>
      </Stack>
    </Stack>
  );
};

export default StatsTab;
